const MAX_ROW_WIDTH = 16;

export type Country = {
  id: string;
  countryName: string;
  callingCode: string;
  iso3: string;
  postalFirst: boolean;
  hasStateInAddress: boolean;
  postalFormat: string;
  states: string[] | null;
};

const AddressField = {
  Attn: "attn",
  Address: "address",
  Address2: "address2",
  City: "city",
  State: "state",
  Zip: "zip",
  Country: "country",
} as const;

/**
 * Build address format based on country properties
 *
 * @param country - Address country
 * @param showAttn - Show Attn field
 */
export function formatAddressFields({
  country,
  showAttn,
  mobileView = false,
}: {
  country: Country;
  showAttn: boolean;
  mobileView?: boolean;
}): { field: string; width: number }[][] {
  const addressFormat = [
    // Attn
    ...(showAttn
      ? [
          [
            {
              field: AddressField.Attn,
              width: MAX_ROW_WIDTH,
            },
          ],
        ]
      : []),

    // Address
    [
      {
        field: AddressField.Address,
        width: MAX_ROW_WIDTH,
      },
    ],

    // Address2/Country
    [
      {
        field: AddressField.Address2,
        width: MAX_ROW_WIDTH,
      },
    ],

    // City/State/Zip
    getCityStateZipRow(country, mobileView),
  ].map((row) => {
    // If row has only one column, set width to max
    if (row.length === 1)
      return [
        {
          field: row[0].field,
          width: MAX_ROW_WIDTH,
        },
      ];
    return row;
  });

  return addressFormat;
}

/**
 * Build city/state/zip fields (the order changes based on country properties)
 *
 * @param country - Address country
 */
function getCityStateZipRow(country: Country, mobileView = false) {
  const city = {
    field: AddressField.City,
    width: mobileView ? MAX_ROW_WIDTH : !country || country.hasStateInAddress ? 7 : 12,
  };
  const state = {
    field: AddressField.State,
    width: mobileView ? MAX_ROW_WIDTH : 5,
  };
  const zip = {
    field: AddressField.Zip,
    width: mobileView ? MAX_ROW_WIDTH : 4,
  };

  if (!country) return [city, state, zip];
  if (country.hasStateInAddress && country.postalFirst) return [zip, city, state];
  if (country.hasStateInAddress && !country.postalFirst) return [city, state, zip];
  if (!country.hasStateInAddress && country.postalFirst) return [zip, city];
  if (!country.hasStateInAddress && !country.postalFirst) return [city, zip];
  return [city, state, zip];
}
